import { useState, useEffect } from "react";
import useCompanyStore from "../../store/useCompanyStore";
import useDepartmentStore from "../../store/useDepartmentStore";
import useDepartmentWiseEmployeeStore from "../../store/useDepartmentWiseEmployeeStore";

const AddDepartment = () => {
  const [selectedCompany, setSelectedCompany] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { companyList, fetchCompanies } = useCompanyStore();
  const { departmentList, getDepartmentList } = useDepartmentStore();
  const { getDepartmentWiseEmployees } = useDepartmentWiseEmployeeStore();

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    getDepartmentList(selectedCompany);
  }, [selectedCompany]);

  const handleDepartmentWiseEmployees = (id) => {
    getDepartmentWiseEmployees(id);
  };

  const resetForm = () => {
    setDepartmentName("");
    setIsActive(true);
    setDepartmentId("");
    setIsEditing(false);
    setError("");
    setSuccess("");
  };

  const handleDepartmentSelect = (e) => {
    const selectedId = e.target.value;
    setDepartmentId(selectedId);
    handleDepartmentWiseEmployees(selectedId);

    if (selectedId) {
      const selectedDepartment = departmentList.find(
        (dept) => dept.id === selectedId,
      );
      setDepartmentName(selectedDepartment?.department_name || "");
      setIsActive(selectedDepartment?.is_active !== false);
      setIsEditing(true);
    } else {
      resetForm();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedCompany) {
      setError("Please select a company");
      return;
    }
    if (!departmentName.trim()) {
      setError("Please enter a department name");
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append(
      "Authorization",
      `Bearer ${localStorage.getItem("accessToken")}`,
    );
    myHeaders.append("Content-Type", "application/json");

    const payload = {
      company_id: companyList.find((c) => c.company_name === selectedCompany)
        ?.id,
      name: departmentName,
      ...(isEditing && { is_active: isActive }),
    };

    const requestOptions = {
      method: isEditing ? "PUT" : "POST",
      headers: myHeaders,
      body: JSON.stringify(payload),
      redirect: "follow",
    };

    try {
      const url = isEditing
        ? `${process.env.REACT_APP_BASE_URL}/departments/${departmentId}`
        : `${process.env.REACT_APP_BASE_URL}/departments`;

      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        throw new Error("Failed to save department");
      }
      const result = await response.json();
      setSuccess(`Department ${isEditing ? "updated" : "added"} successfully`);
      getDepartmentList(selectedCompany);
      resetForm();
    } catch (error) {
      setError(error.message || "An error occurred");
    }
  };

  return (
    <div className="w-full p-7">
      <h1 className="text-2xl font-medium text-gray-800 mb-8">
        {isEditing ? "Update Department" : "Add Department"}
      </h1>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Company
              </label>
              <select
                name="company"
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-hrms"
              >
                <option value="">Select Company</option>
                {companyList?.map((company, index) => (
                  <option value={company.company_name} key={index}>
                    {company.company_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Department (for editing)
              </label>
              <select
                name="department"
                value={departmentId}
                onChange={handleDepartmentSelect}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-hrms"
              >
                <option value="">Select Department</option>
                {departmentList?.map((department, index) => (
                  <option value={department.id} key={index}>
                    {department.department_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department Name
            </label>
            <input
              type="text"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-hrms"
              placeholder="Enter department name"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-primary-hrms border-gray-300 rounded mr-2"
              />
              Active Department
            </label>
          </div>

          {error && <div className="text-red-500 text-sm">{error}</div>}
          {success && <div className="text-green-500 text-sm">{success}</div>}

          <div className="flex gap-4 mt-4">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-primary-hrms text-white rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-primary-hrms"
            >
              {isEditing ? "Update Department" : "Add Department"}
            </button>
            {(departmentName || isEditing) && (
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDepartment;
